export interface RouteState {
  page: string;
  productId?: string;
  blogSlug?: string;
}

const VALID_PAGES = [
  'home',
  'products',
  'services',
  'calculator',
  'about',
  'contact',
  'admin',
  'login',
  'blog',
  'insights',
  'privacy-policy',
  'terms',
  'disclaimer'
];

/**
 * Resolves current route from window.location pathname and hash,
 * seamlessly converting legacy hash URLs to clean URLs.
 */
export function resolveCurrentRoute(): RouteState {
  if (typeof window === 'undefined') {
    return { page: 'home' };
  }

  // 1. Check if user arrived with a legacy hash like #products or #product/xxx or #blog/xxx
  const hash = window.location.hash || '';
  if (hash) {
    const cleanHash = hash.replace(/^#\/?/, '').trim();
    if (cleanHash.startsWith('product/')) {
      const id = cleanHash.replace('product/', '').trim();
      return { page: 'product-detail', productId: id };
    }
    if (cleanHash.startsWith('blog/')) {
      const slug = cleanHash.replace('blog/', '').trim();
      return { page: 'blog-detail', blogSlug: slug };
    }
    if (cleanHash === 'login' || cleanHash === 'admin-login') {
      return { page: 'login' };
    }
    if (VALID_PAGES.includes(cleanHash)) {
      return { page: cleanHash === 'insights' ? 'blog' : cleanHash };
    }
  }

  // 2. Parse clean pathname
  const pathname = window.location.pathname.replace(/^\/+|\/+$/g, ''); // strip leading/trailing slashes

  if (!pathname || pathname === 'home') {
    return { page: 'home' };
  }

  if (pathname === 'login' || pathname === 'admin/login' || pathname === 'admin-login') {
    return { page: 'login' };
  }

  if (pathname.startsWith('product/')) {
    const id = pathname.replace('product/', '').trim();
    return { page: 'product-detail', productId: id || 'milk-pasteurizer-300l' };
  }

  if (pathname.startsWith('products/')) {
    const id = pathname.replace('products/', '').trim();
    if (id) {
      return { page: 'product-detail', productId: id };
    }
    return { page: 'products' };
  }

  if (pathname.startsWith('blog/')) {
    const slug = pathname.replace('blog/', '').trim();
    if (slug) {
      return { page: 'blog-detail', blogSlug: slug };
    }
    return { page: 'blog' };
  }

  if (pathname === 'insights') {
    return { page: 'blog' };
  }

  if (VALID_PAGES.includes(pathname)) {
    return { page: pathname };
  }

  return { page: '404' };
}

/**
 * Builds clean browser URL pathname without any '#' symbol.
 */
export function buildCleanPath(page: string, productIdOrSlug?: string, blogSlug?: string): string {
  if (page === 'home' || page === '/') {
    return '/';
  }
  if (page === 'product-detail' && productIdOrSlug) {
    return `/product/${productIdOrSlug}`;
  }
  if (page === 'blog-detail') {
    const slug = blogSlug || productIdOrSlug;
    return slug ? `/blog/${slug}` : '/blog';
  }
  return `/${page}`;
}
